import React, { useEffect, useRef } from 'react';

export const CALENDLY_DEMO_URL =
  process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/smileconsent-info/30min';

const DEFAULT_EMBED_PARAMS = {
  hide_gdpr_banner: '1',
  background_color: 'ffffff',
  text_color: '0c1b2a',
  primary_color: '1d4ed8',
};

/** Append Calendly embed query params without breaking an existing URL. */
export function calendlyEmbedUrl(baseUrl = CALENDLY_DEMO_URL, extra = {}) {
  try {
    const parsed = new URL(baseUrl);
    Object.entries({ ...DEFAULT_EMBED_PARAMS, ...extra }).forEach(([key, value]) => {
      if (value != null && value !== '') parsed.searchParams.set(key, String(value));
    });
    return parsed.toString();
  } catch {
    return baseUrl;
  }
}

const SCRIPT_ID = 'calendly-widget-script';
const SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const STYLE_ID = 'calendly-widget-style';
const STYLE_HREF = 'https://assets.calendly.com/assets/external/widget.css';

function loadCalendlyStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const link = document.createElement('link');
  link.id = STYLE_ID;
  link.rel = 'stylesheet';
  link.href = STYLE_HREF;
  document.head.appendChild(link);
}

function loadCalendlyScript() {
  loadCalendlyStyle();
  return new Promise((resolve, reject) => {
    if (window.Calendly) {
      resolve();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (existing.getAttribute('data-loaded') === 'true') {
        window.Calendly ? resolve() : reject(new Error('Calendly script loaded but API unavailable'));
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Calendly script failed to load')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    };
    script.onerror = () => reject(new Error('Calendly script failed to load'));
    document.body.appendChild(script);
  });
}

export default function CalendlyEmbed({ url = CALENDLY_DEMO_URL, minHeight = '700px', className = '' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    let active = true;
    const container = containerRef.current;
    if (!container) return undefined;

    loadCalendlyScript()
      .then(() => {
        if (!active || !containerRef.current || !window.Calendly) return;

        containerRef.current.innerHTML = '';
        window.Calendly.initInlineWidget({
          url,
          parentElement: containerRef.current,
        });
      })
      .catch((err) => {
        console.error('Calendly embed failed:', err);
      });

    // Do not clear innerHTML on cleanup — React Strict Mode remounts would leave a blank box.
    return () => {
      active = false;
    };
  }, [url]);

  return (
    <div
      ref={containerRef}
      className={`w-full ${className}`.trim()}
      style={{ minHeight, width: '100%' }}
    />
  );
}
